// One mockup gallery card (Flow 3; docs/phase-0/slice5-component-designs.md §3.3
// `.mockup-card` - a `.card` with no `.pad` so the generated screen image bleeds to the
// card's own edge). Tweak is a short, scoped instruction, never a full regenerate (ux
// spec §6.3); its own `aria-busy` while in flight so a screen-reader user tabbing
// through isn't stalled by an unrelated card's in-flight request (ux spec §6.6).
import { useId, useState } from "react";
import { mediaAssetUrl } from "../api/brand.js";
import { buildCopy } from "../copy/build.js";

export const BrandMockupCard = ({
  screenRef,
  busy = false,
  disabledReason,
  onTweak,
}: {
  readonly screenRef: string;
  /** True while this card's own scoped tweak request is in flight (ux spec §6.6) -
   * other cards stay interactive (a partial-results render, not a page-wide block). */
  readonly busy?: boolean;
  /** Present only for a viewer (role-gated); omitted entirely for an owner/admin. */
  readonly disabledReason?: string;
  readonly onTweak?: (instruction: string) => void;
}): React.JSX.Element => {
  const [editing, setEditing] = useState(false);
  const [instruction, setInstruction] = useState("");
  const disabled = disabledReason !== undefined;
  const reasonId = useId();
  const fieldId = useId();

  return (
    <div className="card mockup-card" data-testid="mockup-card" aria-busy={busy}>
      <img
        src={mediaAssetUrl(screenRef)}
        alt=""
        style={{ aspectRatio: "9 / 16", width: "100%", objectFit: "cover", display: "block" }}
        onError={(event) => {
          event.currentTarget.style.visibility = "hidden";
        }}
      />
      <div className="pad" style={{ padding: 14, borderBlockStart: "1px solid var(--line)" }}>
        {busy ? (
          <p className="muted" style={{ fontSize: 12.5 }} role="status">
            {buildCopy("build.brand.mockups.tweaking")}
          </p>
        ) : editing ? (
          <div className="stack gap8">
            <label className="field-label" htmlFor={fieldId}>
              {buildCopy("build.brand.mockups.tweakCta")}
            </label>
            <textarea
              id={fieldId}
              className="field"
              value={instruction}
              onChange={(event) => {
                setInstruction(event.target.value);
              }}
            />
            <div className="row gap8">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={instruction.trim().length === 0}
                onClick={() => {
                  onTweak?.(instruction.trim());
                  setEditing(false);
                  setInstruction("");
                }}
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setEditing(false);
                  setInstruction("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="tooltip-wrap">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={disabled}
              aria-describedby={disabled ? reasonId : undefined}
              onClick={() => {
                setEditing(true);
              }}
            >
              {buildCopy("build.brand.mockups.tweakCta")}
            </button>
            {disabled ? (
              <span className="tooltip-bubble" id={reasonId} role="tooltip">
                {disabledReason}
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
